import SiteDesignStore from "../stores/SiteDesignStore";
import {
  LAYER_BOUND_FRONT,
  DIRECTION_ADJUSTMENT_MAP,
  DIRECTION_DOWN,
  DIRECTION_UP,
  DROP_AREA_BOTTOM,
  DROP_AREA_TOP,
} from "../constants/LayerConstants";
import AppConstants from "../constants/AppConstants";
import DocumentStore from "../document/DocumentStore";
import { COMMANDS } from "../document/commands";
import IDUtils from "./IDUtils";
import ActiveBlockContainerStore from "@/stores/ActiveBlockContainerStore.mjs";
import { ELEMENT_ORDER } from "@/constants/EntityConstants";
import { getContainerData } from "./GroupUtils";
import Block from "@/entities/Block";

const { ITEM_TYPE_ELEMENT } = AppConstants;

/**
 * @typedef {typeof DIRECTION_UP | typeof DIRECTION_DOWN} Direction
 * @typedef {typeof DROP_AREA_TOP | typeof DROP_AREA_BOTTOM} DropArea
 * @typedef {import("@/entities/ElementContainer").Target} Target
 * @typedef {import("@/types/Selection.mjs").ElementInfo} ElementSelectionInfo
 * @typedef {import("immutable").Map<string, ElementSelectionInfo>} SelectedElements
 */

/**
 * Prepares target props for contained element moves
 * @param {Object} props
 * @param {string} props.baseElemId
 * @param {Direction} props.direction
 * @param {Pick<ElementSelectionInfo, "id">} props.firstElem
 * @param {SelectedElements} props.selectedElements
 * @returns {{ id: string, containedBy: string, dropArea: DropArea } | null}
 */
export const _prepareContainedMove = ({ baseElemId, direction, firstElem, selectedElements }) => {
  const blockContainer = ActiveBlockContainerStore.getContainer();
  const selectedBlock = blockContainer.get("selectedBlock");

  const elementContainer = getContainerData(selectedBlock, firstElem.id);

  const elementOrder = elementContainer.get(ELEMENT_ORDER);

  // find highest or lowest index depending on direction
  const currentIndex = selectedElements.reduce((result, _, id) => {
    const currentId = id.split(".").at(-1);
    const idIndex = elementOrder.indexOf(currentId);
    return result === undefined
      ? idIndex
      : (direction === DIRECTION_UP && idIndex < result) ||
          (direction === DIRECTION_DOWN && idIndex > result)
        ? idIndex
        : result;
  }, undefined);

  const topBound = !currentIndex && direction === DIRECTION_UP;
  const bottomBound = currentIndex === elementOrder.size - 1 && direction === DIRECTION_DOWN;

  // elements area already at the bounds of the list
  if (topBound || bottomBound) return null;

  const newIndex = currentIndex + (direction === DIRECTION_UP ? -1 : 1);
  const clampedIndex = Math.min(Math.max(newIndex, 0), elementOrder.size - 1);
  const newId = elementOrder.get(clampedIndex);
  const targetId = `${baseElemId}.${newId}`;
  const dropArea = direction === DIRECTION_UP ? DROP_AREA_TOP : DROP_AREA_BOTTOM;

  return {
    id: targetId,
    containedBy: elementContainer.get("id"),
    dropArea,
  };
};

/**
 * Moves selected elements up or down in the layer stack
 * @param {Direction} direction - The direction to move the elements (DIRECTION_UP or DIRECTION_DOWN)
 * @returns {void}
 */
export const moveElementLayers = (direction) => {
  const selection = SiteDesignStore.getSelected();
  if (!selection?.get("elements")?.size) {
    return;
  }

  const directionAdjustment = DIRECTION_ADJUSTMENT_MAP[direction] ?? 0;
  const selectedElements = selection.get("elements");
  const selectedElemCount = selectedElements.size;
  const firstElem = selectedElements?.minBy((elem) => elem.index);
  const firstElemId = firstElem.id;

  const containedBy = firstElem?.containedBy;
  const baseElemId = firstElemId.split(".").slice(0, -1).join(".");

  const targetIndex = firstElem.index + (direction === DIRECTION_UP ? selectedElemCount : 0);
  const targetId = `${baseElemId}.${targetIndex}`;
  const dropArea = direction === DIRECTION_UP ? DROP_AREA_BOTTOM : DROP_AREA_TOP;

  const item = {
    id: firstElemId,
    layerType: ITEM_TYPE_ELEMENT,
  };

  const target = {
    id: targetId,
    layerType: ITEM_TYPE_ELEMENT,
    index: targetIndex + directionAdjustment,
    dropArea,
  };

  if (containedBy) {
    const containedTargetProps = _prepareContainedMove({
      baseElemId,
      direction,
      firstElem,
      selectedElements,
    });

    if (containedTargetProps === null) return;

    Object.assign(target, containedTargetProps);
  }

  if (targetIndex > 0 || containedBy) {
    DocumentStore.dispatch({ type: COMMANDS.MOVE_LAYER, payload: { item, target } });
  }
};

/**
 * Prepares the move target for sending contained elements to the front or back of their container
 * @param {Object} props
 * @param {string} props.baseElemId - Path ID of the parent holding the selected elements
 * @param {Block} props.block - Block entity containing the elements
 * @param {boolean} props.isFront - True to move to the front, false to move to the back
 * @param {string} props.referenceElemId - ID of the element used to find the containing parent
 * @param {SelectedElements} props.selectedElements - Selected elements keyed by selection path ID
 * @returns {Target | null}
 */
const _prepareContainedBoundTarget = ({
  baseElemId,
  block,
  isFront,
  referenceElemId,
  selectedElements,
}) =>
  block
    .getContainingParentEntity(referenceElemId)
    ?.getBoundTarget({ baseElemId, isFront, selectedIds: selectedElements.keySeq() }) ?? null;

/**
 * Prepares the move target for sending top level elements to the front or back of the block
 * @param {Object} props
 * @param {string} props.baseElemId - Path ID of the parent holding the selected elements
 * @param {Block} props.block - Block entity containing the elements
 * @param {boolean} props.isFront - True to move to the front, false to move to the back
 * @param {SelectedElements} props.selectedElements - Selected elements keyed by selection path ID
 * @returns {Target | null}
 */
const _prepareTopLevelBoundTarget = ({ baseElemId, block, isFront, selectedElements }) => {
  const size = block.getElementCountAtPath(baseElemId);

  if (!size) return null;

  const selectedCount = selectedElements.size;
  const atBound = selectedElements.every((elem) =>
    isFront ? elem.index >= size - selectedCount : elem.index < selectedCount,
  );

  if (atBound) return null;

  return {
    id: IDUtils.getID(baseElemId, "0"),
    layerType: ITEM_TYPE_ELEMENT,
    index: isFront ? size : 0,
  };
};

/**
 * Moves the selected elements to the front or back of their layer stack
 * @param {"front" | "back"} bound - The bound to move the elements to (LAYER_BOUND_FRONT or LAYER_BOUND_BACK)
 * @returns {void}
 */
export const moveElementsToBound = (bound) => {
  const selectedElements = SiteDesignStore.getSelected()?.get("elements");

  if (!selectedElements?.size) return;

  const referenceElem = selectedElements.first();
  const referenceElemId = referenceElem.id;

  const prepareTarget = referenceElem.containedBy
    ? _prepareContainedBoundTarget
    : _prepareTopLevelBoundTarget;

  const target = prepareTarget({
    baseElemId: IDUtils.getPathID(IDUtils.getArray(referenceElemId).slice(0, -1)),
    block: new Block(ActiveBlockContainerStore.getContainer().get("selectedBlock")),
    isFront: bound === LAYER_BOUND_FRONT,
    referenceElemId,
    selectedElements,
  });

  if (!target) return;

  DocumentStore.dispatch({
    type: COMMANDS.MOVE_LAYER,
    payload: {
      item: {
        id: referenceElemId,
        layerType: ITEM_TYPE_ELEMENT,
      },
      target,
    },
  });
};

export default {
  moveElementLayers,
  moveElementsToBound,
};
